import { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { subDays } from 'date-fns';
import { DateRange } from '@robocorp/components';
import { TimeRangeValues } from '~/constants/timeRanges';
import {
  formatVerboseTimestamp,
  calculateTimespan,
  getDates as getTimespanDates,
  getEndOfDayInTimezone,
} from '~/utils/timeHelper';
import {
  TimeRangeSettings,
  useTimeRangeProvider,
} from '~/components/time/timeRange/useTimeRangeProvider';
import { TimeRangeContext } from '~/components/time/timeRange/context';

const getPreviousFrom = ({ from, timespan }: { from?: Date; timespan?: number }) =>
  from !== undefined && timespan !== undefined ? subDays(from, timespan - 1) : undefined;

const getTimespan = ({ from, to }: { from?: Date; to?: Date }) => {
  if (from === undefined || to === undefined) {
    return undefined;
  }
  return Math.max(Math.floor(calculateTimespan(from, to)), 2);
};

interface PresetProps {
  allTime?: boolean;
}

interface CommonTimeRangeSettings {
  preset?: TimeRangeValues;
  range?: DateRange;
  withBillingPreset: boolean;
  timezone: string;
  timestamp: Date;
}

const useCommonDateRange = ({
  preset,
  range,
  withBillingPreset,
  timezone,
  timestamp,
}: CommonTimeRangeSettings) => {
  const { t } = useTranslation();

  const from = range?.from;
  const to = range?.to;

  const timestampTo = useMemo(
    () => to ?? getEndOfDayInTimezone(timestamp, timezone),
    [to, timestamp, timezone],
  );

  const timespan = useMemo(() => getTimespan({ from, to: timestampTo }), [from, timestampTo]);
  const getDates = useCallback(
    () => getTimespanDates(timespan ?? 0, timestampTo),
    [timespan, timestampTo],
  );

  const previousRange = useMemo(
    () => ({
      from: getPreviousFrom({ from, timespan }),
      to,
    }),
    [from, timespan, to],
  );

  const getPresets = useCallback(
    ({ allTime = true }: PresetProps = {}) =>
      withBillingPreset
        ? [
            ...(allTime ? [{ value: TimeRangeValues.ALL_TIME, label: t('allTime') }] : []),
            { value: TimeRangeValues.CURRENT_BILLING_PERIOD, label: t('thisBillingPeriod') },
            { value: TimeRangeValues.LAST_30_DAYS, label: t('lastDays', { days: 30 }) },
            { value: TimeRangeValues.LAST_7_DAYS, label: t('lastDays', { days: 7 }) },
            { value: TimeRangeValues.LAST_24_HOURS, label: t('lastHours', { hours: 24 }) },
            { value: TimeRangeValues.CUSTOM, label: t('custom') },
          ]
        : [
            ...(allTime ? [{ value: TimeRangeValues.ALL_TIME, label: t('allTime') }] : []),
            { value: TimeRangeValues.THIS_MONTH, label: t('thisMonth') },
            { value: TimeRangeValues.LAST_30_DAYS, label: t('lastDays', { days: 30 }) },
            { value: TimeRangeValues.LAST_7_DAYS, label: t('lastDays', { days: 7 }) },
            { value: TimeRangeValues.LAST_24_HOURS, label: t('lastHours', { hours: 24 }) },
            { value: TimeRangeValues.CUSTOM, label: t('custom') },
          ],
    [t, withBillingPreset],
  );

  const rangeLabel = useMemo(() => {
    if (preset !== TimeRangeValues.CUSTOM || !from) return undefined;
    const fromLabel = formatVerboseTimestamp(from, { timezone, time: false });
    return `${fromLabel} - ${
      to ? formatVerboseTimestamp(to, { timezone, time: false }) : t('now')
    }`;
  }, [from, to, preset, t, timezone]);

  return { getPresets, rangeLabel, timespan: timespan ?? 0, previousRange, getDates };
};

/**
 * Context aware date range settings for usage with filters, tables and charts
 *
 * @example
 * const { preset, range, onPresetChange } = useContextTimeRange();
 */
export const useContextTimeRange = () => {
  const { preset, range, withBillingPreset, timezone, timestamp, ...contextArgs } =
    useContext(TimeRangeContext);
  const commonArgs = useCommonDateRange({
    preset,
    range,
    withBillingPreset,
    timezone,
    timestamp,
  });

  return { preset, range, timestamp, ...contextArgs, ...commonArgs };
};

/**
 * Date range settings for usage with filters, tables and charts
 *
 * @example
 * const { preset, range, onPresetChange } = useTimeRange();
 */
export const useTimeRange = ({
  preset: initialPreset,
  range: initialRange,
}: TimeRangeSettings = {}) => {
  const { preset, range, withBillingPreset, timezone, timestamp, ...contextArgs } =
    useTimeRangeProvider(
      initialRange
        ? {
            preset: TimeRangeValues.CUSTOM,
            range: initialRange,
          }
        : {
            preset: initialPreset,
          },
    );
  const commonArgs = useCommonDateRange({
    preset,
    range,
    withBillingPreset,
    timezone,
    timestamp,
  });

  return { preset, range, timestamp, ...contextArgs, ...commonArgs };
};
